import { renderModal, closeModal } from './ui.mjs';
import { createPayerItem, createAddPersonRow } from './receiptHelpers.mjs';
import { createMonetaryInput, getCurrencyFractionDigits, splitNumber } from './monetaryInput.mjs';
import { formatCurrency } from './utils.mjs';
import t from 'virtual:i18n';

/**
 * Opens a bottom-sheet drawer to select who paid for an expense.
 * Supports both single-payer selection and multi-payer allocation.
 *
 * @param {Object} options
 * @param {HTMLElement} [options.anchorEl] - Anchor element for the modal
 * @param {number} options.amount - Total amount of the expense
 * @param {string} options.currency - ISO 4217 currency code
 * @param {string[]} options.participants - Array of participant names (may be updated by adding new people)
 * @param {number[]} options.payers - Array of participant indices who paid
 * @param {number[]} options.payerAmounts - Array of allocated amounts per payer
 * @param {Function} options.onSave - Callback when payers are saved: (payers, payerAmounts, updatedParticipants) => void
 * @returns {HTMLElement} The modal element
 */
export function openPayerSelectorDrawer({
    anchorEl = null,
    amount,
    currency,
    participants,
    payers,
    payerAmounts,
    onSave
}) {
    const modalAnchor = anchorEl || document.getElementById('modal-anchor') || document.body;

    const listContainer = document.createElement('div');
    listContainer.style.cssText = 'display: flex; flex-direction: column;';

    let payerSheetModal = null;

    // Local state copies
    let localParticipants = [...participants];
    let localPayers = [...payers];
    let localPayerAmounts = [...payerAmounts];

    const renderMultiPayer = () => {
        listContainer.innerHTML = '';
        
        // Instructions
        const infoText = document.createElement('div');
        infoText.innerText = "Select who paid and how much:";
        infoText.style.cssText = 'font-size: 14px; color: #666; margin-bottom: 12px; padding: 0 16px; font-family: inherit;';
        listContainer.appendChild(infoText);

        const totalAmount = amount || 0;
        const fractionDigits = getCurrencyFractionDigits(currency);

        // A map of local allocations: participant_index -> double amount
        const localAllocations = {};
        localParticipants.forEach((_, idx) => {
            const payerIdx = localPayers.indexOf(idx);
            if (payerIdx !== -1) {
                localAllocations[idx] = localPayerAmounts[payerIdx];
            }
        });

        const rowsContainer = document.createElement('div');
        rowsContainer.style.cssText = 'display: flex; flex-direction: column; overflow-y: auto; max-height: 40vh;';

        const activeRowInputs = {}; // index -> monetaryInput element

        const updateSummary = () => {
            let currentSum = 0;
            Object.keys(localAllocations).forEach(key => {
                currentSum += localAllocations[key];
            });
            currentSum = parseFloat(currentSum.toFixed(fractionDigits));

            const diff = totalAmount - currentSum;
            const preciseDiff = parseFloat(diff.toFixed(fractionDigits));

            sumValSpan.innerText = formatCurrency(currentSum, currency);
            
            if (Math.abs(preciseDiff) < 0.01) {
                diffSpan.innerHTML = `✓ matched`;
                diffSpan.style.color = 'green';
                confirmBtn.disabled = false;
                confirmBtn.style.opacity = '1';
                confirmBtn.style.cursor = 'pointer';
            } else {
                const diffSignText = preciseDiff > 0 
                    ? `${formatCurrency(preciseDiff, currency)} remaining`
                    : `${formatCurrency(Math.abs(preciseDiff), currency)} over`;
                diffSpan.innerText = diffSignText;
                diffSpan.style.color = 'red';
                confirmBtn.disabled = true;
                confirmBtn.style.opacity = '0.5';
                confirmBtn.style.cursor = 'not-allowed';
            }
        };

        localParticipants.forEach((name, index) => {
            const row = document.createElement('div');
            row.style.cssText = `
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 12px 16px;
                border-bottom: 1px solid #F3F0F4;
            `;

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.style.cssText = 'transform: scale(1.2); cursor: pointer;';
            checkbox.checked = (index in localAllocations);

            const avatar = document.createElement('div');
            avatar.style.cssText = `
                width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
                background-color: #e8e8e8; color: #555;
                display: flex; align-items: center; justify-content: center;
                font-weight: 600; font-size: 14px;
            `;
            avatar.textContent = name.charAt(0).toUpperCase();

            const nameEl = document.createElement('div');
            nameEl.style.cssText = 'flex: 1; font-size: 15px; color: #1D1B20; font-weight: 500;';
            nameEl.textContent = name;

            const amountInput = createMonetaryInput({
                id: `payer-amount-${index}`,
                value: localAllocations[index] || 0,
                placeholder: '0.00',
                currency: currency,
                className: ''
            });

            Object.assign(amountInput.style, {
                width: '100px',
                padding: '8px',
                textAlign: 'right',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontWeight: 'bold',
                display: (index in localAllocations) ? 'block' : 'none'
            });

            activeRowInputs[index] = amountInput;

            checkbox.onchange = () => {
                if (checkbox.checked) {
                    localAllocations[index] = 0;
                    amountInput.style.display = 'block';
                    amountInput.setMonetaryValue(0);
                    // Auto focus
                    setTimeout(() => amountInput.focus(), 50);
                } else {
                    delete localAllocations[index];
                    amountInput.style.display = 'none';
                }
                updateSummary();
            };

            amountInput.addEventListener('input', () => {
                const val = amountInput.getRawValue();
                if (!isNaN(val) && val >= 0) {
                    localAllocations[index] = val;
                    updateSummary();
                }
            });

            row.appendChild(checkbox);
            row.appendChild(avatar);
            row.appendChild(nameEl);
            row.appendChild(amountInput);
            rowsContainer.appendChild(row);
        });

        listContainer.appendChild(rowsContainer);

        // Shortcuts Bar (Split Equally)
        const shortcutsBar = document.createElement('div');
        shortcutsBar.style.cssText = 'display: flex; justify-content: flex-end; padding: 12px 16px 4px 16px;';
        const splitEquallyBtn = document.createElement('button');
        splitEquallyBtn.innerText = 'Split Equally';
        Object.assign(splitEquallyBtn.style, {
            background: 'none',
            border: 'none',
            color: 'var(--color-primary)',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
            textDecoration: 'underline'
        });
        splitEquallyBtn.onclick = () => {
            const checkedIndices = [];
            localParticipants.forEach((_, idx) => {
                if (idx in localAllocations) {
                    checkedIndices.push(idx);
                }
            });

            if (checkedIndices.length > 0) {
                const parts = splitNumber(totalAmount, checkedIndices.length, currency);
                checkedIndices.forEach((idx, i) => {
                    localAllocations[idx] = parts[i];
                    if (activeRowInputs[idx]) {
                        activeRowInputs[idx].setMonetaryValue(parts[i]);
                    }
                });
                updateSummary();
            }
        };
        shortcutsBar.appendChild(splitEquallyBtn);
        listContainer.appendChild(shortcutsBar);

        // Summary Section
        const summaryDiv = document.createElement('div');
        summaryDiv.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 12px 16px;
            border-top: 1px solid #f0f0f0;
            margin-top: 8px;
            background-color: #fafafa;
        `;
        
        const totalRow = document.createElement('div');
        totalRow.style.cssText = 'display: flex; justify-content: space-between; font-size: 14px; color: #555;';
        totalRow.innerHTML = `<span>Total expense:</span><span style="font-weight: 600;">${formatCurrency(totalAmount, currency)}</span>`;

        const sumRow = document.createElement('div');
        sumRow.style.cssText = 'display: flex; justify-content: space-between; font-size: 14px;';
        const sumLabel = document.createElement('span');
        sumLabel.innerText = 'Sum of payments:';
        const sumValSpan = document.createElement('span');
        sumValSpan.style.fontWeight = 'bold';
        sumRow.appendChild(sumLabel);
        sumRow.appendChild(sumValSpan);

        const diffRow = document.createElement('div');
        diffRow.style.cssText = 'display: flex; justify-content: space-between; font-size: 13px; font-weight: 500;';
        const diffLabel = document.createElement('span');
        diffLabel.innerText = 'Status:';
        const diffSpan = document.createElement('span');
        diffRow.appendChild(diffLabel);
        diffRow.appendChild(diffSpan);

        summaryDiv.appendChild(totalRow);
        summaryDiv.appendChild(sumRow);
        summaryDiv.appendChild(diffRow);
        listContainer.appendChild(summaryDiv);

        // Action Buttons Footer
        const footerDiv = document.createElement('div');
        footerDiv.style.cssText = 'display: flex; justify-content: space-between; padding: 16px; gap: 12px;';

        const backBtn = document.createElement('button');
        backBtn.innerText = 'Back';
        Object.assign(backBtn.style, {
            flex: '1',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px'
        });
        backBtn.onclick = () => {
            // If they go back, we reset back to single payer
            localPayers = [payers[0] || 0];
            localPayerAmounts = [totalAmount];
            renderList();
        };

        const confirmBtn = document.createElement('button');
        confirmBtn.innerText = t.confirm || 'Confirm';
        Object.assign(confirmBtn.style, {
            flex: '1',
            padding: '12px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'var(--color-primary)',
            color: 'white',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px'
        });
        confirmBtn.onclick = () => {
            const finalPayers = [];
            const finalPayerAmounts = [];
            Object.keys(localAllocations).forEach(key => {
                const idx = parseInt(key);
                finalPayers.push(idx);
                finalPayerAmounts.push(localAllocations[idx]);
            });
            
            if (onSave) {
                onSave(finalPayers, finalPayerAmounts, localParticipants);
            }
            if (payerSheetModal) closeModal(payerSheetModal);
        };

        footerDiv.appendChild(backBtn);
        footerDiv.appendChild(confirmBtn);
        listContainer.appendChild(footerDiv);

        updateSummary();
    };

    const renderList = () => {
        listContainer.innerHTML = '';

        localParticipants.forEach((name, index) => {
            const isSelected = localPayers.length === 1 && localPayers[0] === index;

            const row = createPayerItem({
                name,
                index,
                isSelected,
                onClick: () => {
                    const finalPayers = [index];
                    const finalPayerAmounts = [amount || 0];
                    if (onSave) {
                        onSave(finalPayers, finalPayerAmounts, localParticipants);
                    }
                    if (payerSheetModal) closeModal(payerSheetModal);
                }
            });

            listContainer.appendChild(row);
        });

        // Add "Multiple people paid..." row
        const multiPayerRow = document.createElement('div');
        multiPayerRow.style.cssText = `
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 14px 16px;
            cursor: pointer;
            border-bottom: 1px solid #F3F0F4;
            transition: background-color 0.15s ease;
            color: var(--color-primary);
            font-weight: 600;
        `;
        const isMultiSelected = localPayers.length > 1;
        if (isMultiSelected) {
            multiPayerRow.style.backgroundColor = 'rgba(var(--color-primary-rgb), 0.07)';
        }

        multiPayerRow.addEventListener('mouseenter', () => {
            if (!isMultiSelected) multiPayerRow.style.backgroundColor = 'rgba(var(--color-primary-rgb), 0.04)';
        });
        multiPayerRow.addEventListener('mouseleave', () => {
            multiPayerRow.style.backgroundColor = isMultiSelected ? 'rgba(var(--color-primary-rgb), 0.07)' : '';
        });

        const multiAvatar = document.createElement('div');
        multiAvatar.style.cssText = `
            width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
            background-color: ${isMultiSelected ? 'var(--color-primary)' : 'rgba(var(--color-primary-rgb), 0.1)'};
            color: ${isMultiSelected ? 'white' : 'var(--color-primary)'};
            display: flex; align-items: center; justify-content: center;
            font-weight: bold; font-size: 16px;
        `;
        multiAvatar.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
        `;

        const multiText = document.createElement('div');
        multiText.style.cssText = 'flex: 1; font-size: 16px;';
        multiText.textContent = 'Multiple people paid...';

        multiPayerRow.appendChild(multiAvatar);
        multiPayerRow.appendChild(multiText);

        multiPayerRow.onclick = () => {
            if (localPayers.length <= 1) {
                localPayers = [localPayers[0] || 0];
                localPayerAmounts = [amount || 0];
            }
            renderMultiPayer();
        };

        listContainer.appendChild(multiPayerRow);

        const addRow = createAddPersonRow((newName) => {
            const newIndex = localParticipants.length;
            localParticipants.push(newName);

            // Immediately set them as the single payer
            const finalPayers = [newIndex];
            const finalPayerAmounts = [amount || 0];
            if (onSave) {
                onSave(finalPayers, finalPayerAmounts, localParticipants);
            }
            if (payerSheetModal) closeModal(payerSheetModal);
        });
        listContainer.appendChild(addRow);
    };

    if (localPayers.length > 1) {
        renderMultiPayer();
    } else {
        renderList();
    }

    payerSheetModal = renderModal({
        anchorEl: modalAnchor,
        title: t.whoPaidForIt,
        body: listContainer,
        buttons: []
    });

    return payerSheetModal;
}
